import { useState } from "react";
import { toast } from "sonner";
import { User, Lock, Mail, ShieldCheck, KeyRound } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Button, Input, Badge } from "@/components/kit";

export default function Profile() {
  const { user, setUser, loadUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);

  const [cur, setCur] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const hasPassword = !!user?.has_password;

  const saveName = async () => {
    if (name.trim().length < 2) { toast.error("Nama minimal 2 karakter"); return; }
    setSavingName(true);
    try {
      const { data } = await api.post("/auth/profile", { name: name.trim() });
      setUser(data);
      toast.success("Nama diperbarui");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingName(false); }
  };

  const savePassword = async () => {
    if (pw1.length < 8) { toast.error("Password minimal 8 karakter"); return; }
    if (pw1 !== pw2) { toast.error("Konfirmasi password tidak cocok"); return; }
    setSavingPw(true);
    try {
      if (hasPassword) {
        if (!cur) { toast.error("Masukkan password saat ini"); setSavingPw(false); return; }
        await api.post("/auth/change-password", { current_password: cur, new_password: pw1 });
        toast.success("Password berhasil diubah");
      } else {
        await api.post("/auth/set-password", { password: pw1 });
        toast.success("Password berhasil dibuat! Sekarang kamu bisa login pakai email & password.");
      }
      setCur(""); setPw1(""); setPw2("");
      await loadUser();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingPw(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="profile-page">
      <div>
        <span className="inline-flex items-center gap-3"><span className="w-10 h-10 rounded-xl bg-sky-100 grid place-items-center shrink-0"><User className="w-5 h-5 text-sky-600" /></span><h1 className="font-display text-3xl font-extrabold">Pengaturan Profil</h1></span>
        <p className="text-slate-500 mt-1">Kelola identitas & keamanan akun kamu.</p>
      </div>

      {/* Account info */}
      <Card className="p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-sky-100 grid place-items-center text-sky-700 font-extrabold text-xl">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-slate-800 flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /> {user?.email}</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge color="sky" className="capitalize">{user?.plan}</Badge>
              <Badge color="slate">{user?.auth_provider === "google" ? "Login Google" : "Email & Password"}</Badge>
            </div>
          </div>
        </div>
      </Card>

      {/* Name */}
      <Card className="p-6">
        <h3 className="font-display font-bold flex items-center gap-2 mb-4"><User className="w-4 h-4 text-sky-500" /> Nama Tampilan</h3>
        <div className="flex gap-3">
          <Input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nama lengkap" />
          <Button data-testid="profile-name-save" onClick={saveName} loading={savingName}>Simpan</Button>
        </div>
      </Card>

      {/* Password */}
      <Card className="p-6">
        <h3 className="font-display font-bold flex items-center gap-2 mb-1">
          {hasPassword ? <Lock className="w-4 h-4 text-sky-500" /> : <KeyRound className="w-4 h-4 text-sky-500" />}
          {hasPassword ? "Ubah Password" : "Buat Password"}
        </h3>
        {!hasPassword && (
          <div className="flex items-start gap-2 text-sm text-sky-800 bg-sky-50 border border-sky-200 rounded-xl px-3 py-2.5 mt-2 mb-4">
            <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Akun kamu login lewat Google. Buat password agar bisa login dengan <b>kedua cara</b> (Google & email/password) memakai email yang sama.</span>
          </div>
        )}
        <div className="space-y-3 mt-3">
          {hasPassword && (
            <div>
              <label className="text-xs font-semibold text-slate-500">Password saat ini</label>
              <Input data-testid="profile-current-password" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1" placeholder="••••••••" />
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">Password baru</label>
              <Input data-testid="profile-new-password" type="password" value={pw1} onChange={(e) => setPw1(e.target.value)} className="mt-1" placeholder="Min. 8 karakter" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Konfirmasi password</label>
              <Input data-testid="profile-confirm-password" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1" placeholder="Ulangi password" />
            </div>
          </div>
          <Button data-testid="profile-password-save" onClick={savePassword} loading={savingPw}>
            {hasPassword ? "Ubah Password" : "Buat Password"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
